import { useEffect, useState, useCallback } from "react";
import { api } from "@/sales/lib/api";
import { useAuth } from "@/sales/auth/AuthContext";
import KPIDashboard from "@/sales/components/KPIDashboard";
import KanbanBoard from "@/sales/components/KanbanBoard";
import DealDialog from "@/sales/components/DealDialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Eye, Filter } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [deals, setDeals] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [accountmanagers, setAccountmanagers] = useState([]);
  const [filterEigenaar, setFilterEigenaar] = useState("alle");
  const { isGast, isBestuurslid } = useAuth();

  const fetchAll = useCallback(async () => {
    try {
      const params = filterEigenaar && filterEigenaar !== "alle"
        ? { params: { eigenaar: filterEigenaar } }
        : {};
      const [dealsRes, kpisRes] = await Promise.all([
        api.get("/deals", params),
        api.get("/kpis", params),
      ]);
      setDeals(dealsRes.data);
      setKpis(kpisRes.data);
    } catch (err) {
      toast.error("Kon data niet laden");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterEigenaar]);

  // Load deal-owner roster (super-admins + accountmanagers) for the filter
  // dropdown. Viewers are excluded because they can't be assigned as eigenaar.
  useEffect(() => {
    if (!isBestuurslid) return;
    api
      .get("/auth/users")
      .then((r) => {
        const owners = (r.data || []).filter((u) => u.role !== "gast");
        // Sort: super-admins first, then alphabetical by display_name
        owners.sort((a, b) => {
          if (a.role !== b.role) return a.role === "bestuurslid" ? -1 : 1;
          return (a.display_name || "").localeCompare(b.display_name || "");
        });
        setAccountmanagers(owners);
      })
      .catch(() => setAccountmanagers([]));
  }, [isBestuurslid]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleStageChange = async (dealId, newStage, outcome = null) => {
    if (isGast) return;
    // Optimistic update
    const prev = deals;
    setDeals(deals.map((d) => (d.id === dealId ? { ...d, stage: newStage, outcome } : d)));
    try {
      await api.patch(`/deals/${dealId}/stage`, { stage: newStage, outcome });
      const kpisParams = filterEigenaar && filterEigenaar !== "alle"
        ? { params: { eigenaar: filterEigenaar } }
        : {};
      const kpisRes = await api.get("/kpis", kpisParams);
      setKpis(kpisRes.data);
      toast.success("Deal verplaatst");
    } catch (err) {
      setDeals(prev);
      toast.error("Verplaatsen mislukt");
    }
  };

  const handleOpenNew = () => {
    if (isGast) return;
    setEditingDeal(null);
    setDialogOpen(true);
  };

  const handleEdit = (deal) => {
    if (isGast) return;
    setEditingDeal(deal);
    setDialogOpen(true);
  };

  const handleSave = async (payload) => {
    try {
      if (editingDeal) {
        await api.put(`/deals/${editingDeal.id}`, payload);
        toast.success("Deal bijgewerkt");
      } else {
        await api.post("/deals", payload);
        toast.success("Organisatie toegevoegd");
      }
      setDialogOpen(false);
      setEditingDeal(null);
      fetchAll();
    } catch (err) {
      const reason =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Onbekende fout";
      toast.error("Opslaan mislukt", { description: String(reason) });
      console.error("[save deal] failed:", err?.response?.status, err?.response?.data);
    }
  };

  const handleDelete = async (dealId) => {
    try {
      await api.delete(`/deals/${dealId}`);
      toast.success("Deal verwijderd");
      setDialogOpen(false);
      setEditingDeal(null);
      fetchAll();
    } catch (err) {
      const reason = err?.response?.data?.detail || err?.message || "Onbekende fout";
      toast.error("Verwijderen mislukt", { description: String(reason) });
    }
  };

  return (
    <main className="w-full max-w-[1680px] mx-auto px-6 md:px-10 pb-12">
        <section className="mb-10 fade-up pt-2">
          <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-medium text-[#F8FAFC]">
                Quantitative Dashboard
              </h2>
              <p className="text-sm text-[#94A3B8] mt-1">
                Real-time monitoring van de B2B sales pipeline · Government accounts
              </p>
            </div>
            <div className="flex items-center gap-4 ml-auto">
              {isBestuurslid && (
                <div
                  data-testid="kpi-filter-wrap"
                  className="flex items-center gap-3 bg-[#142539] border border-[#C0C0C0]/40 rounded-lg pl-4 pr-2 py-2 shadow-lg"
                  style={{ boxShadow: "0 0 0 1px rgba(192,192,192,0.08), 0 8px 20px -8px rgba(0,0,0,0.6)" }}
                >
                  <Filter size={14} className="text-[#C0C0C0]" />
                  <label
                    htmlFor="kpi-filter-trigger"
                    className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#C0C0C0] whitespace-nowrap"
                  >
                    Filter op Accountmanager
                  </label>
                  <Select
                    value={filterEigenaar}
                    onValueChange={(v) => setFilterEigenaar(v)}
                  >
                    <SelectTrigger
                      id="kpi-filter-trigger"
                      data-testid="kpi-filter-trigger"
                      className="min-w-[200px] bg-[#1C3A5A] border border-[#C0C0C0]/60 hover:border-white text-white text-sm font-medium rounded-md px-4 py-2 transition-colors"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C3A5A] border-[#C0C0C0]/40 text-white">
                      <SelectItem
                        value="alle"
                        className="text-white focus:bg-[#2A4D77] focus:text-white py-2"
                      >
                        Alle resellers
                      </SelectItem>
                      {accountmanagers.map((u) => (
                        <SelectItem
                          key={u.username}
                          value={u.username}
                          data-testid={`kpi-filter-option-${u.username}`}
                          className="text-white focus:bg-[#2A4D77] focus:text-white py-2"
                        >
                          <span className="inline-flex items-center gap-2">
                            <span>{u.display_name}</span>
                            <span
                              className={`text-[9px] uppercase tracking-[0.14em] font-semibold rounded px-1.5 py-0.5 ${
                                u.role === "bestuurslid"
                                  ? "bg-gradient-to-r from-[#A07527] to-[#D4A24C] text-[#1C1407]"
                                  : "bg-[#0B1521] text-[#93C5FD] border border-[#3B6FB5]/60"
                              }`}
                            >
                              {u.role === "bestuurslid" ? "Super-Admin" : "Accountmgr"}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {kpis && (
                <div className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.18em] text-[#94A3B8]">
                  <div data-testid="kpi-active-count">
                    <span className="text-[#C0C0C0] font-semibold mr-1">{kpis.deal_counts.active}</span>
                    Active
                  </div>
                  <div data-testid="kpi-won-count">
                    <span className="text-[#C0C0C0] font-semibold mr-1">{kpis.deal_counts.won}</span>
                    Won
                  </div>
                  <div data-testid="kpi-lost-count">
                    <span className="text-[#C0C0C0] font-semibold mr-1">{kpis.deal_counts.lost}</span>
                    Lost
                  </div>
                </div>
              )}
            </div>
          </div>
          <KPIDashboard kpis={kpis} loading={loading} />
        </section>

        <section className="fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="flex items-baseline justify-between mb-6 gap-4">
            <div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-medium text-[#F8FAFC]">
                Sales Funnel
              </h2>
              <p className="text-sm text-[#94A3B8] mt-1">
                Sleep deals tussen fases · 5-staps B2B verkoopproces
              </p>
            </div>
            <Button
              data-testid="add-lead-btn"
              onClick={handleOpenNew}
              disabled={isGast}
              className={`bg-[#C0C0C0] hover:bg-white text-[#0B1521] font-medium rounded px-4 py-2 transition-all border-0 flex-shrink-0 ${
                isGast ? "hidden" : ""
              }`}
            >
              <Plus size={16} className="mr-1.5" />
              Nieuwe Organisatie
            </Button>
            {isGast && (
              <span
                data-testid="viewer-readonly-badge"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] bg-[#1E293B] border border-[#475569] rounded-full px-3 py-1.5"
              >
                <Eye size={11} /> Alleen lezen
              </span>
            )}
          </div>
          <KanbanBoard
            deals={deals}
            loading={loading}
            onStageChange={handleStageChange}
            onCardClick={handleEdit}
            onAddLead={isGast ? null : handleOpenNew}
            readOnly={isGast}
          />
        </section>

      <DealDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditingDeal(null);
        }}
        deal={editingDeal}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </main>
  );
}
